import { EntityType, ViewerNode } from '../models/graph-viewer.models';
import { ENTITY_COLORS, ENTITY_LABELS } from './graph-viewer-data.util';
import { resolveGraphPlatformIconSpec } from '../../../../services/link-analysis/platform-icon.util';

/** Maltego-style entity colors used on the graph canvas. */
export const GRAPH_ICON_COLORS: Record<EntityType, string> = {
  person: '#4f7dc9',
  email: '#2aa8a0',
  phone: '#2ea043',
  social: '#7c5cbf',
  location: '#c45c4a',
  image: '#5b8fb9',
};

export const GRAPH_EDGE_COLOR_LIGHT = 'rgba(100, 116, 139, 0.48)';
export const GRAPH_EDGE_COLOR_DARK = 'rgba(148, 163, 184, 0.38)';
export const GRAPH_EDGE_ACTIVE = '#f5a623';
export const GRAPH_EDGE_PATH = '#f59e0b';

const GENERIC_TYPES = new Set([
  'phone',
  'email',
  'person',
  'social',
  'location',
  'image',
  'starter',
  'osint_source',
  'social_profile',
  'source_type_hub',
  'unknown',
]);

function slugify(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function pickCategoryCandidate(value: unknown): string | null {
  const slug = slugify(String(value || ''));
  if (!slug || slug.length > 28) return null;
  if (GENERIC_TYPES.has(slug)) return null;
  return slug;
}

/** Lowercase category shown above the icon (e.g. personal, mobile, facebook). */
export function getGraphCategoryLabel(node: ViewerNode | null | undefined): string {
  if (!node) return 'entity';
  const details = node.details || {};
  const raw = (node as ViewerNode & { raw?: any }).raw || {};
  const metadata = raw.metadata || {};
  const candidates = [
    details['fieldType'],
    details['phoneType'],
    details['emailType'],
    details['category'],
    details['role'],
    details['relation'],
    metadata.fieldType,
    metadata.phoneType,
    metadata.category,
    metadata.role,
    details['platform'],
    metadata.platform,
    metadata.sourceName,
    metadata.hubSourceType,
    raw.type,
  ];
  for (const candidate of candidates) {
    const slug = pickCategoryCandidate(candidate);
    if (slug) return slug;
  }
  const typeLabel = ENTITY_LABELS[node.type] || node.type || 'entity';
  return slugify(typeLabel) || 'entity';
}

export function getGraphIconColor(
  type: EntityType | string | undefined,
  fallback?: string | null,
): string {
  if (fallback) return fallback;
  const key = String(type || '') as EntityType;
  return GRAPH_ICON_COLORS[key] || ENTITY_COLORS[key] || '#64748b';
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function paintEmailAt(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
): void {
  ctx.font = `700 ${size * 1.85}px "Segoe UI", "Helvetica Neue", Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = color;
  ctx.fillText('@', x, y + size * 0.04);
}

function paintPhoneKeypad(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
): void {
  const w = size * 1.08;
  const h = size * 1.82;
  const left = x - w / 2;
  const top = y - h / 2;
  const radius = w * 0.18;

  roundRectPath(ctx, left, top, w, h, radius);
  ctx.fillStyle = color;
  ctx.fill();

  const speakerW = w * 0.34;
  const speakerH = Math.max(h * 0.045, 1.4);
  roundRectPath(ctx, x - speakerW / 2, top + h * 0.1, speakerW, speakerH, speakerH / 2);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.82)';
  ctx.fill();

  const keysTop = top + h * 0.28;
  const keysH = h * 0.62;
  const keysW = w * 0.58;
  const cols = 3;
  const rows = 4;
  const colStep = keysW / cols;
  const rowStep = keysH / rows;
  const buttonR = Math.min(colStep, rowStep) * 0.28;

  ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const kx = x - keysW / 2 + (col + 0.5) * colStep;
      const ky = keysTop + (row + 0.5) * rowStep;
      ctx.beginPath();
      ctx.arc(kx, ky, buttonR, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

function paintLocationPin(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
): void {
  const top = y - size * 0.92;
  const radius = size * 0.58;
  ctx.beginPath();
  ctx.moveTo(x, y + size * 0.95);
  ctx.bezierCurveTo(
    x - radius * 1.15,
    y + size * 0.15,
    x - radius,
    top + radius * 0.1,
    x,
    top + radius * 0.05,
  );
  ctx.bezierCurveTo(
    x + radius,
    top + radius * 0.1,
    x + radius * 1.15,
    y + size * 0.15,
    x,
    y + size * 0.95,
  );
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(x, y - size * 0.28, size * 0.22, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.fill();
}

function paintPersonSilhouette(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y - size * 0.42, size * 0.38, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(x - size * 0.78, y + size * 0.98);
  ctx.quadraticCurveTo(x - size * 0.72, y + size * 0.08, x, y + size * 0.08);
  ctx.quadraticCurveTo(x + size * 0.72, y + size * 0.08, x + size * 0.78, y + size * 0.98);
  ctx.closePath();
  ctx.fill();
}

function paintImageFrame(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
): void {
  const w = size * 1.7;
  const h = size * 1.35;
  roundRectPath(ctx, x - w / 2, y - h / 2, w, h, size * 0.12);
  ctx.fillStyle = color;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(x - w * 0.22, y - h * 0.18, size * 0.14, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(x - w * 0.42, y + h * 0.32);
  ctx.lineTo(x - w * 0.08, y - h * 0.02);
  ctx.lineTo(x + w * 0.12, y + h * 0.18);
  ctx.lineTo(x + w * 0.22, y + h * 0.02);
  ctx.lineTo(x + w * 0.42, y + h * 0.32);
  ctx.closePath();
  ctx.fill();
}

function paintGlobe(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
): void {
  ctx.beginPath();
  ctx.arc(x, y, size * 0.82, 0, Math.PI * 2);
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(size * 0.14, 1.4);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(x, y, size * 0.32, size * 0.82, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x - size * 0.82, y);
  ctx.lineTo(x + size * 0.82, y);
  ctx.stroke();
}

/** Draw a type-specific Maltego-style icon (no circular node body). */
export function paintGraphTypeIcon(
  ctx: CanvasRenderingContext2D,
  type: EntityType | string,
  x: number,
  y: number,
  size: number,
  color: string,
): void {
  const kind = String(type || '').toLowerCase();
  if (kind === 'email' || kind === 'email_hint' || kind === 'aggregate_emails') {
    paintEmailAt(ctx, x, y, size, color);
    return;
  }
  if (kind === 'phone' || kind === 'phone_hint' || kind === 'aggregate_phones' || kind === 'starter') {
    paintPhoneKeypad(ctx, x, y, size, color);
    return;
  }
  if (kind === 'location' || kind === 'address') {
    paintLocationPin(ctx, x, y, size, color);
    return;
  }
  if (kind === 'image' || kind === 'face_result' || kind === 'face_image' || kind === 'image_query' || kind === 'aggregate_photos') {
    paintImageFrame(ctx, x, y, size, color);
    return;
  }
  if (kind === 'person' || kind === 'aggregate_names') {
    paintPersonSilhouette(ctx, x, y, size, color);
    return;
  }
  if (kind === 'query_root' || kind === 'starter') {
    paintPhoneKeypad(ctx, x, y, size, color);
    return;
  }
  if (kind === 'social' || kind === 'username') {
    paintPersonSilhouette(ctx, x, y, size, color);
    return;
  }
  paintGlobe(ctx, x, y, size, color);
}

export function paintBookmarkRibbon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
): void {
  const bx = x + size * 0.55;
  const by = y - size * 1.05;
  const w = size * 0.34;
  const h = size * 0.58;
  ctx.beginPath();
  ctx.moveTo(bx, by);
  ctx.lineTo(bx + w, by);
  ctx.lineTo(bx + w, by + h);
  ctx.lineTo(bx + w / 2, by + h * 0.72);
  ctx.lineTo(bx, by + h);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

export function paintSquarePhoto(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  size: number,
): void {
  const side = size * 1.85;
  const left = x - side / 2;
  const top = y - side / 2;
  ctx.save();
  roundRectPath(ctx, left, top, side, side, size * 0.12);
  ctx.clip();
  ctx.drawImage(img, left, top, side, side);
  ctx.restore();
  roundRectPath(ctx, left, top, side, side, size * 0.12);
  ctx.strokeStyle = 'rgba(90, 96, 104, 0.28)';
  ctx.lineWidth = Math.max(size * 0.04, 0.6);
  ctx.stroke();
}

export function paintBrandGlyph(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  size: number,
): void {
  const side = size * 1.9;
  ctx.drawImage(img, x - side / 2, y - side / 2, side, side);
}

/** Colored tile used until a local logo loads, or when the platform has no file in public/osint-logo. */
export function paintPlatformBrandIcon(
  ctx: CanvasRenderingContext2D,
  platform: string,
  x: number,
  y: number,
  size: number,
): void {
  const spec = resolveGraphPlatformIconSpec(platform);
  const side = size * 1.9;
  const left = x - side / 2;
  const top = y - side / 2;
  const radius = side * (20 / 96);
  const canUsePath = typeof Path2D !== 'undefined';

  ctx.save();
  roundRectPath(ctx, left, top, side, side, radius);
  ctx.fillStyle = spec.accent;
  ctx.fill();

  if (spec.simplePath && canUsePath) {
    const pad = side * (20 / 96);
    const glyph = Math.max(side - pad * 2, 1);
    ctx.translate(left + pad, top + pad);
    ctx.scale(glyph / 24, glyph / 24);
    ctx.fillStyle = '#ffffff';
    ctx.fill(new Path2D(spec.simplePath));
  } else if (spec.visual.iconPath && canUsePath) {
    ctx.translate(left, top);
    ctx.scale(side / 96, side / 96);
    const path = new Path2D(spec.visual.iconPath);
    const fill = spec.visual.iconFill && spec.visual.iconFill !== 'none' ? spec.visual.iconFill : 'none';
    if (fill !== 'none') {
      ctx.fillStyle = fill;
      ctx.fill(path);
    } else {
      ctx.strokeStyle = spec.visual.iconStroke || '#ffffff';
      ctx.lineWidth = spec.visual.iconStrokeWidth || 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke(path);
    }
  } else {
    const text = spec.visual.iconText || '?';
    ctx.fillStyle = spec.visual.iconTextColor || '#ffffff';
    ctx.font = `800 ${side * (text.length >= 3 ? 0.28 : 0.38)}px Inter, Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x, y + side * 0.02);
  }
  ctx.restore();
}

export function paintSelectionHalo(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
): void {
  ctx.beginPath();
  ctx.arc(x, y, size + 5, 0, Math.PI * 2);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.7;
  ctx.stroke();
}

export function insetLine(
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  startPad: number,
  endPad: number,
): { x1: number; y1: number; x2: number; y2: number } | null {
  const dx = tx - sx;
  const dy = ty - sy;
  const len = Math.hypot(dx, dy);
  if (len < 1) return null;
  const ux = dx / len;
  const uy = dy / len;
  const usable = len - startPad - endPad;
  if (usable < 2) return null;
  return {
    x1: sx + ux * startPad,
    y1: sy + uy * startPad,
    x2: tx - ux * endPad,
    y2: ty - uy * endPad,
  };
}

export function paintArrowHead(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  size: number,
  color: string,
): void {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const spread = Math.PI / 7;
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - size * Math.cos(angle - spread), y2 - size * Math.sin(angle - spread));
  ctx.lineTo(x2 - size * Math.cos(angle + spread), y2 - size * Math.sin(angle + spread));
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

export function paintGraphNodeLabels(
  ctx: CanvasRenderingContext2D,
  options: {
    category: string;
    value: string;
    x: number;
    y: number;
    size: number;
    globalScale: number;
    categoryColor: string;
    valueColor: string;
  },
): void {
  const { category, value, x, y, size, globalScale, categoryColor, valueColor } = options;
  const categorySize = Math.max(8.5 / globalScale, 2.6);
  const valueSize = Math.max(10.5 / globalScale, 3.2);
  const categoryGap = 5.5 / globalScale;
  const valueGap = Math.max(size * 0.22, 10 / globalScale);

  ctx.textAlign = 'center';
  if (category) {
    ctx.font = `500 ${categorySize}px "Segoe UI", "Helvetica Neue", Arial, sans-serif`;
    ctx.fillStyle = categoryColor;
    ctx.textBaseline = 'bottom';
    ctx.fillText(category, x, y - size - categoryGap);
  }
  if (value) {
    ctx.font = `600 ${valueSize}px "Segoe UI", "Helvetica Neue", Arial, sans-serif`;
    ctx.fillStyle = valueColor;
    ctx.textBaseline = 'top';
    ctx.fillText(value, x, y + size + valueGap);
  }
}

export function getGraphPointerBounds(
  x: number,
  y: number,
  size: number,
): { left: number; top: number; width: number; height: number } {
  const width = size * 3.6;
  const height = size * 5.1;
  return {
    left: x - width / 2,
    top: y - height / 2 - size * 0.2,
    width,
    height,
  };
}
